import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiEdit, FiCode } from "react-icons/fi";
import { FIELD_TYPES } from "../FormBuilder";

const getFieldTypes = (t) => [
  { value: "text", label: t("text") || "텍스트" },
  { value: "textarea", label: t("textarea") || "텍스트 영역" },
  { value: "number", label: t("number") || "숫자" },
  { value: "select", label: t("select") || "선택 목록" },
  { value: "checkbox", label: t("checkbox") || "체크박스" },
  { value: "radio", label: t("radio") || "라디오 버튼" },
  { value: "date", label: t("date") || "날짜" },
  { value: "time", label: t("time") || "시간" },
  { value: "datetime", label: t("datetime") || "날짜 및 시간" },
  { value: "file", label: t("file") || "파일 업로드" },
  { value: "calculated", label: t("calculated") || "계산 필드" },
  { value: "timediff", label: t("timediff") || "시간 차이" },
];

const getDependencyConditions = (t) => [
  { value: "equals", label: t("equals") || "같음 (==)" },
  { value: "notEquals", label: t("notEquals") || "같지 않음 (!=)" },
  { value: "contains", label: t("contains") || "포함함" },
  { value: "notContains", label: t("notContains") || "포함하지 않음" },
  { value: "greaterThan", label: t("greaterThan") || "보다 큼 (>)" },
  { value: "lessThan", label: t("lessThan") || "보다 작음 (<)" },
  {
    value: "greaterThanOrEqual",
    label: t("greaterThanOrEqual") || "이상 (>=)",
  },
  { value: "lessThanOrEqual", label: t("lessThanOrEqual") || "이하 (<=)" },
];

/**
 * 필드 에디터 컴포넌트
 * 선택된 필드의 속성을 편집합니다.
 */
const FieldEditor = ({
  selectedField,
  availableFields = [],
  onUpdateField,
  onUpdateDependency,
  onRemoveDependency,
  onDeleteField,
  onRenameFieldId,
  t = (key) => key, // 기본 번역 함수
}) => {
  // 필드 타입과 의존성 조건 목록 가져오기
  const fieldTypes = getFieldTypes(t);
  const dependencyConditions = getDependencyConditions(t);

  const [fieldData, setFieldData] = useState({
    label: "",
    type: "text",
    required: false,
    placeholder: "",
    options: [],
    min: "",
    max: "",
    step: 1,
    showAge: false,
    dependencyType: "simple",
    dependsOn: "",
    dependsOnValue: "",
    complexDependency: {
      condition: "AND",
      fields: [],
    },
    formula: "",
    dependsOnFields: [],
    dependencyEnabled: false,
    startField: "",
    endField: "",
    format: "minutes",
  });

  const [newOption, setNewOption] = useState({ label: "", value: "" });
  const [previewValue, setPreviewValue] = useState("");

  // TimeDiff 관련 상태를 컴포넌트 최상위 레벨로 이동
  const [showStartFieldList, setShowStartFieldList] = useState(false);
  const [showEndFieldList, setShowEndFieldList] = useState(false);
  const [searchStartTerm, setSearchStartTerm] = useState("");
  const [searchEndTerm, setSearchEndTerm] = useState("");

  // 선택된 필드 데이터로 상태 초기화
  useEffect(() => {
    if (selectedField) {
      // 기존 필드 데이터로 초기화
      const initialData = {
        ...selectedField,
        // 복잡한 의존성 데이터 구조 초기화
        dependencyType: selectedField.dependency
          ? typeof selectedField.dependency === "object" &&
            selectedField.dependency.condition
            ? "complex"
            : "simple"
          : "simple",
        // 단순 의존성 필드
        dependsOn:
          selectedField.dependency &&
          typeof selectedField.dependency === "string"
            ? selectedField.dependency
            : selectedField.dependency && selectedField.dependency.fieldId
            ? selectedField.dependency.fieldId
            : "",
        // 단순 의존성 값
        dependsOnValue:
          selectedField.dependency && selectedField.dependency.value
            ? selectedField.dependency.value
            : "",
        // 복잡한 의존성 설정
        complexDependency:
          selectedField.dependency &&
          typeof selectedField.dependency === "object" &&
          selectedField.dependency.condition
            ? selectedField.dependency
            : { condition: "AND", fields: [] },
        // 계산 필드 설정
        formula: selectedField.formula || "",
        dependsOnFields: selectedField.dependsOn || [],
        dependencyEnabled: !!selectedField.dependency,
        startField: selectedField.startField || "",
        endField: selectedField.endField || "",
        format: selectedField.format || "minutes",
      };

      setFieldData(initialData);
    }
  }, [selectedField.id]);

  // watch simple dependency changes
  useEffect(() => {
    if (fieldData.dependencyEnabled || fieldData.dependsOn) {
      if (fieldData.dependsOn) {
        onUpdateDependency &&
          onUpdateDependency({
            fieldId: fieldData.dependsOn,
            value: fieldData.dependsOnValue,
          });
      }
    }
  }, [fieldData.dependsOn, fieldData.dependsOnValue]);

  useEffect(() => {
    if (fieldData.type === "calculated") {
      try {
        const val = evaluate(fieldData.formula || "0", {});
        setPreviewValue(val);
      } catch (e) {
        setPreviewValue("수식 오류");
      }
    }
  }, [fieldData.formula]);

  // 필드가 선택되지 않은 경우
  if (!selectedField) {
    return (
      <div className="field-editor empty-state">
        <p>{t("selectFieldOrAddNew")}</p>
      </div>
    );
  }

  // 일반 입력 변경 핸들러
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const updatedFieldData = {
      ...fieldData,
      [name]: type === "checkbox" ? checked : value,
    };

    setFieldData(updatedFieldData);
    if (!["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(name)) {
      onUpdateField(updatedFieldData);
    }
  };

  // 숫자 입력 변경 핸들러
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const numValue = value === "" ? "" : Number(value);

    const updatedFieldData = {
      ...fieldData,
      [name]: numValue,
    };

    setFieldData(updatedFieldData);
    if (!["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(name)) {
      onUpdateField(updatedFieldData);
    }
  };

  // 옵션 추가 핸들러
  const handleAddOption = () => {
    if (!newOption.label.trim() || !newOption.value.trim()) return;

    const updatedOptions = [...(fieldData.options || []), { ...newOption }];
    const updatedFieldData = {
      ...fieldData,
      options: updatedOptions,
    };

    setFieldData(updatedFieldData);
    setNewOption({ label: "", value: "" });
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes("options")
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 옵션 제거 핸들러
  const handleRemoveOption = (index) => {
    const updatedOptions = [...fieldData.options];
    updatedOptions.splice(index, 1);

    const updatedFieldData = {
      ...fieldData,
      options: updatedOptions,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes("options")
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 옵션 변경 핸들러
  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...fieldData.options];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };

    const updatedFieldData = {
      ...fieldData,
      options: updatedOptions,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes("options")
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 복잡한 의존성 조건 추가 핸들러
  const handleAddComplexDependency = () => {
    const updatedComplexDependency = { ...fieldData.complexDependency };
    updatedComplexDependency.fields = [
      ...updatedComplexDependency.fields,
      { field: "", value: "" },
    ];

    const updatedFieldData = {
      ...fieldData,
      complexDependency: updatedComplexDependency,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(
        "complexDependency"
      )
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 복잡한 의존성 조건 제거 핸들러
  const handleRemoveComplexDependency = (index) => {
    const updatedComplexDependency = { ...fieldData.complexDependency };
    updatedComplexDependency.fields = [...updatedComplexDependency.fields];
    updatedComplexDependency.fields.splice(index, 1);

    const updatedFieldData = {
      ...fieldData,
      complexDependency: updatedComplexDependency,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(
        "complexDependency"
      )
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 복잡한 의존성 조건 변경 핸들러
  const handleComplexDependencyChange = (index, field, value) => {
    const updatedComplexDependency = { ...fieldData.complexDependency };
    updatedComplexDependency.fields = [...updatedComplexDependency.fields];
    updatedComplexDependency.fields[index] = {
      ...updatedComplexDependency.fields[index],
      [field]: value,
    };

    const updatedFieldData = {
      ...fieldData,
      complexDependency: updatedComplexDependency,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(
        "complexDependency"
      )
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 의존성 타입 변경 핸들러
  const handleDependencyTypeChange = (e) => {
    const dependencyType = e.target.value;

    const updatedFieldData = {
      ...fieldData,
      dependencyType,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(
        "dependencyType"
      )
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 의존성 실제 적용 함수 (필드 업데이트시)
  const handleApplyDependency = () => {
    let dependency = null;

    if (fieldData.dependencyType === "simple") {
      // 단순 의존성 적용
      if (fieldData.dependsOn) {
        dependency = {
          fieldId: fieldData.dependsOn,
          value: fieldData.dependsOnValue,
        };
      }
    } else if (fieldData.dependencyType === "complex") {
      // 복잡한 의존성 적용 (AND/OR 조건)
      if (fieldData.complexDependency.fields.length > 0) {
        dependency = { ...fieldData.complexDependency };
      }
    }

    // 의존성 업데이트
    if (dependency) {
      onUpdateDependency(dependency);
    } else {
      onRemoveDependency();
    }
  };

  // 의존성 제거 핸들러
  const handleRemoveDependency = () => {
    // 의존성 필드 초기화
    const updatedFieldData = {
      ...fieldData,
      dependsOn: "",
      dependsOnValue: "",
      complexDependency: {
        condition: "AND",
        fields: [],
      },
      dependencyType: "simple",
      dependencyEnabled: false,
      startField: "",
      endField: "",
      format: "minutes",
    };

    setFieldData(updatedFieldData);
    onRemoveDependency();
  };

  // 계산 필드 의존성 추가
  const handleAddCalculationDependency = (fieldId) => {
    if (!fieldId || fieldData.dependsOnFields.includes(fieldId)) return;

    const updatedDependsOnFields = [...fieldData.dependsOnFields, fieldId];
    const updatedFieldData = {
      ...fieldData,
      dependsOnFields: updatedDependsOnFields,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(
        "dependsOnFields"
      )
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  // 계산 필드 의존성 제거
  const handleRemoveCalculationDependency = (fieldId) => {
    const updatedDependsOnFields = fieldData.dependsOnFields.filter(
      (id) => id !== fieldId
    );
    const updatedFieldData = {
      ...fieldData,
      dependsOnFields: updatedDependsOnFields,
    };

    setFieldData(updatedFieldData);
    if (
      !["dependsOn", "dependsOnValue", "dependencyEnabled"].includes(
        "dependsOnFields"
      )
    ) {
      onUpdateField(updatedFieldData);
    }
  };

  const insertText = (text) => {
    const textarea = document.getElementById("field-formula");
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue =
      textarea.value.substring(0, start) + text + textarea.value.substring(end);
    handleChange({ target: { name: "formula", value: newValue } });
    // 커서 이동
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
  };

  // TimeDiff 필드 설정
  const renderTimeDiffSettings = () => {
    // 검색 가능한 필드 목록 필터링
    const filteredStartFields = availableFields.filter((field) =>
      field.toLowerCase().includes(searchStartTerm.toLowerCase())
    );
    const filteredEndFields = availableFields.filter((field) =>
      field.toLowerCase().includes(searchEndTerm.toLowerCase())
    );

    return (
      <div className="property-group">
        <h4>시간 차이 설정</h4>

        {/* 시작 시간 필드 선택 */}
        <div className="field-select-group">
          <label>시작 시간</label>
          <div className="field-select-container">
            <input
              type="text"
              value={fieldData.startField || ""}
              placeholder="필드 선택 또는 'now' 입력"
              onChange={(e) => {
                const value = e.target.value;
                if (value === "now") {
                  handleChange({
                    target: { name: "startField", value: "now" },
                  });
                } else {
                  handleChange({
                    target: { name: "startField", value: value },
                  });
                }
                setSearchStartTerm(value);
              }}
              onFocus={() => setShowStartFieldList(true)}
              onBlur={() => setTimeout(() => setShowStartFieldList(false), 200)}
            />
            {showStartFieldList && (
              <div className="field-dropdown">
                <input
                  type="text"
                  placeholder="필드 검색..."
                  value={searchStartTerm}
                  onChange={(e) => setSearchStartTerm(e.target.value)}
                  className="field-search"
                />
                <div className="field-list">
                  <div
                    className="field-option"
                    onClick={() => {
                      handleChange({
                        target: { name: "startField", value: "now" },
                      });
                      setShowStartFieldList(false);
                    }}
                  >
                    현재 시각
                  </div>
                  {filteredStartFields.map((field) => (
                    <div
                      key={field}
                      className="field-option"
                      onClick={() => {
                        handleChange({
                          target: { name: "startField", value: field },
                        });
                        setShowStartFieldList(false);
                      }}
                    >
                      {field}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 종료 시간 필드 선택 */}
        <div className="field-select-group">
          <label>종료 시간</label>
          <div className="field-select-container">
            <input
              type="text"
              value={fieldData.endField || ""}
              placeholder="필드 선택 또는 'now' 입력"
              onChange={(e) => {
                const value = e.target.value;
                if (value === "now") {
                  handleChange({ target: { name: "endField", value: "now" } });
                } else {
                  handleChange({ target: { name: "endField", value: value } });
                }
                setSearchEndTerm(value);
              }}
              onFocus={() => setShowEndFieldList(true)}
              onBlur={() => setTimeout(() => setShowEndFieldList(false), 200)}
            />
            {showEndFieldList && (
              <div className="field-dropdown">
                <input
                  type="text"
                  placeholder="필드 검색..."
                  value={searchEndTerm}
                  onChange={(e) => setSearchEndTerm(e.target.value)}
                  className="field-search"
                />
                <div className="field-list">
                  <div
                    className="field-option"
                    onClick={() => {
                      handleChange({
                        target: { name: "endField", value: "now" },
                      });
                      setShowEndFieldList(false);
                    }}
                  >
                    현재 시각
                  </div>
                  {filteredEndFields.map((field) => (
                    <div
                      key={field}
                      className="field-option"
                      onClick={() => {
                        handleChange({
                          target: { name: "endField", value: field },
                        });
                        setShowEndFieldList(false);
                      }}
                    >
                      {field}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 표시 단위 선택 */}
        <div className="field-select-group">
          <label>표시 단위</label>
          <select
            value={fieldData.displayUnit || "minutes"}
            onChange={(e) =>
              handleChange({
                target: { name: "displayUnit", value: e.target.value },
              })
            }
          >
            <option value="minutes">분</option>
            <option value="hours">시간</option>
            <option value="days">일</option>
            <option value="months">개월</option>
            <option value="years">년</option>
            <option value="age">나이</option>
          </select>
        </div>

        {/* 절댓값 사용 여부 */}
        <div className="field-select-group">
          <label>
            <input
              type="checkbox"
              checked={fieldData.useAbsoluteValue || false}
              onChange={(e) =>
                handleChange({
                  target: { name: "useAbsoluteValue", value: e.target.checked },
                })
              }
            />
            절댓값 사용 (음수 무시)
          </label>
        </div>
      </div>
    );
  };

  // 필드 ID 변경 함수
  const handleFieldIdChange = (e) => {
    const newId = e.target.value.trim();
    if (newId && newId !== selectedField.id) {
      onRenameFieldId(selectedField.id, newId);
    }
  };

  return (
    <div className="field-editor">
      <div className="panel-header">
        <h3>필드 설정</h3>
      </div>

      <div className="editor-form">
        <div className="form-group">
          <label htmlFor="field-id">필드 ID</label>
          <input
            id="field-id"
            type="text"
            className="form-control"
            value={selectedField.id || ""}
            onChange={handleFieldIdChange}
            placeholder="필드 ID"
          />
        </div>
        {/* 기본 필드 정보 */}
        <div className="form-group">
          <label htmlFor="field-label">필드 라벨</label>
          <input
            id="field-label"
            type="text"
            name="label"
            className="form-control"
            value={fieldData.label || ""}
            onChange={handleChange}
            placeholder="필드 라벨"
          />
        </div>

        <div className="form-group">
          <label htmlFor="field-type">필드 타입</label>
          <input
            id="field-type"
            type="text"
            className="form-control"
            value={
              fieldTypes.find((t) => t.value === fieldData.type)?.label ||
              fieldData.type
            }
            disabled
          />
        </div>

        <div className="form-group checkbox-group">
          <input
            id="field-required"
            type="checkbox"
            name="required"
            checked={fieldData.required || false}
            onChange={handleChange}
          />
          <label htmlFor="field-required">필수 입력</label>
        </div>

        {/* 필드 타입별 추가 설정 */}
        {fieldData.type !== "checkbox" && fieldData.type !== "calculated" && (
          <div className="form-group">
            <label htmlFor="field-placeholder">플레이스홀더</label>
            <input
              id="field-placeholder"
              type="text"
              name="placeholder"
              className="form-control"
              value={fieldData.placeholder || ""}
              onChange={handleChange}
              placeholder="플레이스홀더 텍스트"
            />
          </div>
        )}

        {/* 숫자 필드 설정 */}
        {fieldData.type === "number" && (
          <div className="form-row">
            <div className="form-group half">
              <label htmlFor="field-min">최소값</label>
              <input
                id="field-min"
                type="number"
                name="min"
                className="form-control"
                value={fieldData.min}
                onChange={handleNumberChange}
                placeholder="최소값"
              />
            </div>
            <div className="form-group half">
              <label htmlFor="field-max">최대값</label>
              <input
                id="field-max"
                type="number"
                name="max"
                className="form-control"
                value={fieldData.max}
                onChange={handleNumberChange}
                placeholder="최대값"
              />
            </div>
          </div>
        )}

        {fieldData.type === "number" && (
          <div className="form-group">
            <label htmlFor="field-step">단계(Step)</label>
            <input
              id="field-step"
              type="number"
              name="step"
              className="form-control"
              value={fieldData.step}
              onChange={handleNumberChange}
              placeholder="단계 값 (예: 0.1)"
              step="0.1"
            />
          </div>
        )}

        {/* 날짜 필드 설정 */}
        {fieldData.type === "date" && (
          <div className="form-group checkbox-group">
            <input
              id="field-show-age"
              type="checkbox"
              name="showAge"
              checked={fieldData.showAge}
              onChange={handleChange}
            />
            <label htmlFor="field-show-age">나이 자동 계산 표시</label>
          </div>
        )}

        {/* 선택 옵션 필드 설정 */}
        {(fieldData.type === "select" ||
          fieldData.type === "radio" ||
          fieldData.type === "checkbox") && (
          <div className="options-section">
            <label>옵션 목록</label>

            <div className="options-list">
              {fieldData.options &&
                fieldData.options.map((option, index) => (
                  <div key={index} className="option-item">
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="라벨"
                        value={option.label}
                        onChange={(e) =>
                          handleOptionChange(index, "label", e.target.value)
                        }
                        className="form-control"
                      />
                      <input
                        type="text"
                        placeholder="값"
                        value={option.value}
                        onChange={(e) =>
                          handleOptionChange(index, "value", e.target.value)
                        }
                        className="form-control"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
            </div>

            <div className="option-item add-option">
              <div className="option-inputs">
                <input
                  type="text"
                  placeholder="새 옵션 라벨"
                  value={newOption.label}
                  onChange={(e) =>
                    setNewOption({ ...newOption, label: e.target.value })
                  }
                  className="form-control"
                />
                <input
                  type="text"
                  placeholder="새 옵션 값"
                  value={newOption.value}
                  onChange={(e) =>
                    setNewOption({ ...newOption, value: e.target.value })
                  }
                  className="form-control"
                />
              </div>
              <button
                type="button"
                className="btn-icon"
                onClick={handleAddOption}
              >
                <FiPlus />
              </button>
            </div>
          </div>
        )}

        {/* 계산 필드 설정 */}
        {fieldData.type === "calculated" && (
          <div className="calculation-section">
            <div className="form-group">
              <label htmlFor="field-formula">계산식</label>
              <textarea
                id="field-formula"
                name="formula"
                className="form-control"
                value={fieldData.formula}
                onChange={handleChange}
                placeholder="예: (formData.height / 100) * (formData.height / 100) / formData.weight"
                rows="3"
              ></textarea>
              <small className="help-text">
                다른 필드 값을 참조하려면 formData.필드ID 형식을 사용하세요.
              </small>
            </div>

            <div className="form-group">
              <label>의존 필드</label>
              <div className="dependency-fields">
                <select
                  className="form-control"
                  onChange={(e) =>
                    handleAddCalculationDependency(e.target.value)
                  }
                  value=""
                >
                  <option value="">-- 필드 선택 --</option>
                  {availableFields
                    .filter(
                      (id) =>
                        id !== selectedField.id &&
                        !fieldData.dependsOnFields.includes(id)
                    )
                    .map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                </select>
              </div>

              {fieldData.dependsOnFields &&
                fieldData.dependsOnFields.length > 0 && (
                  <div className="dependency-list">
                    <label>현재 의존 필드:</label>
                    <ul>
                      {fieldData.dependsOnFields.map((fieldId) => (
                        <li key={fieldId}>
                          {fieldId}
                          <button
                            type="button"
                            className="btn-icon btn-sm"
                            onClick={() =>
                              handleRemoveCalculationDependency(fieldId)
                            }
                          >
                            <FiTrash2 />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>

            {/* 간단 수식 빌더 */}
            <div className="formula-builder">
              <label>빠른 입력</label>
              <div className="operator-buttons" style={{ marginBottom: 4 }}>
                {["+", "-", "*", "/", "(", ")"].map((op) => (
                  <button
                    type="button"
                    key={op}
                    className="btn btn-outline btn-sm"
                    onClick={() => insertText(op)}
                  >
                    {op}
                  </button>
                ))}
              </div>
              <div
                className="field-insert"
                style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
              >
                {availableFields.map((fid) => (
                  <button
                    key={fid}
                    type="button"
                    className="btn btn-outline btn-xs"
                    onClick={() => insertText(`formData.${fid}`)}
                  >
                    {fid}
                  </button>
                ))}
              </div>
            </div>

            {/* 실시간 결과 미리보기 */}
            <div className="form-group">
              <label>미리보기</label>
              <div className="calculated-preview">{previewValue}</div>
            </div>
          </div>
        )}

        {/* 조건부 표시 설정 */}
        <div className="dependency-section">
          <h4>조건부 표시</h4>
          <small className="help-text">
            특정 "의존 필드"의 값이 아래 조건에 일치할 때만 현재 필드를 화면에
            표시합니다. 예를 들어 의존 필드가 "gender"이고 조건 값이 "Male" 일
            때, gender 값이 Male 인 경우에만 이 필드가 노출됩니다. 여러 값을
            쉼표로 구분하면 (예: yes,maybe) 그 중 하나와 일치할 때 표시됩니다.
          </small>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="field-dependency-toggle"
              checked={fieldData.dependencyEnabled}
              onChange={(e) => {
                const checked = e.target.checked;
                setFieldData({
                  ...fieldData,
                  dependencyEnabled: checked,
                });

                if (checked) {
                  onUpdateDependency({
                    fieldId: fieldData.dependsOn,
                    value: fieldData.dependsOnValue,
                  });
                } else {
                  onRemoveDependency();
                }
              }}
            />
            <label htmlFor="field-dependency-toggle">조건부로 표시</label>
          </div>

          {(fieldData.dependsOn || fieldData.dependencyEnabled) && (
            <>
              <div className="form-group">
                <label htmlFor="field-dep-select">의존 필드</label>
                <div
                  className="field-select-container"
                  style={{ display: "flex", gap: "8px" }}
                >
                  <input
                    id="field-dep-select"
                    className="form-control"
                    value={fieldData.dependsOn || ""}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "dependsOn", value: e.target.value },
                      })
                    }
                    placeholder="필드 ID 직접 입력 또는 선택"
                    list="field-dep-list"
                  />
                  <select
                    className="form-control"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleChange({
                          target: { name: "dependsOn", value: e.target.value },
                        });
                      }
                    }}
                    value=""
                  >
                    <option value="">-- 필드 선택 --</option>
                    {availableFields
                      .filter((id) => id !== selectedField.id)
                      .map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                  </select>
                </div>
                <datalist id="field-dep-list">
                  {availableFields
                    .filter((id) => id !== selectedField.id)
                    .map((id) => (
                      <option key={id} value={id} />
                    ))}
                </datalist>
                <small className="help-text">
                  필드 ID를 직접 입력하거나 드롭다운에서 선택할 수 있습니다.
                </small>
              </div>

              {fieldData.dependsOn && (
                <div className="form-group">
                  <label htmlFor="field-dep-value">조건 값(쉼표로 다중)</label>
                  <input
                    id="field-dep-value"
                    type="text"
                    className="form-control"
                    value={fieldData.dependsOnValue || ""}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "dependsOnValue",
                          value: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* TimeDiff 필드 설정 */}
        {fieldData.type === "timediff" && renderTimeDiffSettings()}

        {/* 필드 삭제 버튼 */}
        <div className="field-actions">
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              if (window.confirm(t("confirmDeleteField"))) {
                onDeleteField();
              }
            }}
          >
            <FiTrash2 /> 필드 삭제
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldEditor;
